import { useState } from 'react'
import { useGetAdminTicketsQuery, useUpdateTicketMutation, useGetSupportStatsQuery } from '../../store/api/supportApi'
import type { SupportTicket } from '../../store/api/supportApi'
import { AdminRefreshBtn } from './AdminRefreshBtn'
import { Dropdown, Textarea, PillTabs } from '../../components/ui'
import { timeAgoShort } from '../../lib/avatar'

export function AdminSupport() {
  const [statusFilter, setStatusFilter] = useState('open')
  const [typeFilter, setTypeFilter]     = useState('')
  const [expandedId, setExpandedId]     = useState<string | null>(null)
  const [noteInputs, setNoteInputs]     = useState<Record<string, string>>({})

  const { data, isLoading, refetch } = useGetAdminTicketsQuery({ status: statusFilter || undefined, issueType: typeFilter || undefined })
  const { data: statsData } = useGetSupportStatsQuery()
  const [updateTicket] = useUpdateTicketMutation()

  const tickets  = data?.tickets ?? []
  const byStatus = statsData?.byStatus ?? {}
  const total    = Object.values(byStatus).reduce((a, b) => a + b, 0)

  const priorityColor: Record<string, string> = { high: 'var(--error)', normal: 'var(--warning)', low: '#6b7280' }
  const statusColor:   Record<string, string> = { open: 'var(--info)', in_progress: 'var(--warning)', resolved: 'var(--success)', closed: '#6b7280' }
  const issueIcon: Record<string, string> = { bug: '🐛', account: '👤', content: '🚩', developer: '🛠️', feedback: '💬' }

  const handleUpdate = async (id: string, changes: { status?: string; priority?: string; adminNote?: string }) => {
    await updateTicket({ id, ...changes }).unwrap().catch(() => {})
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.1rem' }}>🎟️ Support Tickets</h2>
          <p style={{ margin: '0.2rem 0 0', fontSize: '0.78rem', color: 'var(--text-muted)' }}>{total} total tickets</p>
        </div>
        <AdminRefreshBtn onClick={() => void refetch()} loading={isLoading} />
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        {Object.entries(byStatus).map(([status, count]) => (
          <span key={status} style={{ padding: '0.25rem 0.65rem', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700, background: `${statusColor[status] ?? '#888'}18`, color: statusColor[status] ?? '#888', border: `1px solid ${statusColor[status] ?? '#888'}33` }}>
            {status.replace('_', ' ')} {count}
          </span>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <PillTabs
          tabs={['', 'open', 'in_progress', 'resolved', 'closed'].map(s => ({ key: s, label: s ? s.replace('_', ' ') : 'All' }))}
          active={statusFilter}
          onChange={setStatusFilter}
          size="sm"
        />
        <Dropdown
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
          options={['bug', 'account', 'content', 'developer', 'feedback'].map(t => ({ value: t, label: `${issueIcon[t]} ${t}` }))}
          placeholder="All types"
          style={{ padding: '0.3rem 0.65rem', fontSize: '0.75rem', width: 'auto' }}
        />
      </div>

      {isLoading && <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</div>}
      {!isLoading && tickets.length === 0 && <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.875rem' }}>No tickets found for the current filters.</div>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {tickets.map(ticket => {
          const isExpanded = expandedId === ticket.id
          return (
            <div key={ticket.id} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', borderLeft: `3px solid ${priorityColor[ticket.priority] ?? '#888'}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', cursor: 'pointer' }} onClick={() => setExpandedId(isExpanded ? null : ticket.id)}>
                <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>{issueIcon[ticket.issueType] ?? '💬'}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ticket.subject}</div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.15rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <span>{ticket.email}</span>
                    {ticket.user?.profile?.displayName && <span>· {ticket.user.profile.displayName}</span>}
                    <span>· {timeAgoShort(ticket.createdAt)}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexShrink: 0 }}>
                  <span style={{ padding: '0.15rem 0.5rem', borderRadius: 20, fontSize: '0.65rem', fontWeight: 700, background: `${statusColor[ticket.status] ?? '#888'}18`, color: statusColor[ticket.status] ?? '#888' }}>{ticket.status.replace('_', ' ')}</span>
                  <span style={{ padding: '0.15rem 0.5rem', borderRadius: 20, fontSize: '0.65rem', fontWeight: 700, background: `${priorityColor[ticket.priority] ?? '#888'}18`, color: priorityColor[ticket.priority] ?? '#888' }}>{ticket.priority}</span>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{isExpanded ? '▲' : '▼'}</span>
                </div>
              </div>

              {isExpanded && (
                <div style={{ padding: '0 1rem 1rem', borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', lineHeight: 1.6, paddingTop: '0.75rem', whiteSpace: 'pre-wrap' }}>{ticket.description}</div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    {(['Status', 'Priority'] as const).map(field => (
                      <div key={field} style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        <label style={{ fontSize: '0.68rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{field}</label>
                        <Dropdown
                          value={field === 'Status' ? ticket.status : ticket.priority}
                          onChange={e => void handleUpdate(ticket.id, field === 'Status' ? { status: e.target.value } : { priority: e.target.value })}
                          options={field === 'Status'
                            ? ['open', 'in_progress', 'resolved', 'closed'].map(s => ({ value: s, label: s.replace('_', ' ') }))
                            : ['low', 'normal', 'high'].map(p => ({ value: p, label: p }))
                          }
                          style={{ padding: '0.3rem 0.6rem', fontSize: '0.78rem', width: 'auto' }}
                        />
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                    <label style={{ fontSize: '0.68rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Admin Note</label>
                    <Textarea
                      value={noteInputs[ticket.id] ?? ticket.adminNote ?? ''}
                      onChange={e => setNoteInputs(prev => ({ ...prev, [ticket.id]: e.target.value }))}
                      placeholder="Internal note (not visible to user)..."
                      rows={2}
                      style={{ fontSize: '0.8rem', minHeight: 'unset' }}
                    />
                    <button onClick={() => void handleUpdate(ticket.id, { adminNote: noteInputs[ticket.id] ?? ticket.adminNote ?? '' })} style={{ alignSelf: 'flex-start', padding: '0.3rem 0.75rem', background: 'var(--accent-primary)', color: 'white', border: 'none', borderRadius: 6, fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}>Save Note</button>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
